/**
 * Per-client branding for the planner SPA.
 *
 * The brand is DATA served by the backend (`/api/public-config`), so one build
 * serves every client — the deployment's BRAND_ID decides what you see. Loaded
 * once before React mounts so nothing flashes the wrong client's name/colours.
 */

const API_BASE_URL =
  process.env.REACT_APP_API_URL || window.location.origin + '/api';

/** Used until the backend responds, and if it never does. */
const FALLBACK = {
  id: 'cor',
  org: { name: 'Creative Options Regina', short: 'COR' },
  apps: { planner: { name: 'COR Support Planner' } },
  logo: { web: '/brands/cor/logo.png', web_max_height_px: 52 },
  colors: {},
  features: {},
};

let current = FALLBACK;

export const brand = () => current;
export const orgName = () => (current.org && current.org.name) || FALLBACK.org.name;
export const appName = () =>
  (current.apps && current.apps.planner && current.apps.planner.name) || orgName();
export const logoSrc = () => (current.logo && current.logo.web) || FALLBACK.logo.web;
/** The client's staff email domain, for placeholders: email@<domain>. */
export const emailDomain = () => (current.org && current.org.email_domain) || 'example.org';
/** Square mark for small slots — the full logo may be wide and would squash. */
export const logoMark = () => `/brands/${current.id || 'cor'}/favicon-192.png`;
export const hasFeature = (name) => Boolean(current.features && current.features[name]);

function applyBrand(b) {
  const root = document.documentElement;
  const c = b.colors || {};

  // The planner's palette variables are COR-named for historical reasons; the
  // NAMES stay, the VALUES follow whichever brand is deployed.
  const map = {
    '--cor-purple': c.primary,
    '--cor-purple-dark': c.primary_dark,
    '--cor-pink': c.accent,
    '--cor-green': c.highlight,
    '--cor-blue': c.info,
    '--cor-yellow': c.warm,
    '--brand-primary': c.primary,
    '--brand-accent': c.accent,
  };
  Object.keys(map).forEach((key) => {
    if (map[key]) root.style.setProperty(key, map[key]);
  });

  document.title = appName();

  // index.html ships COR's favicons because CRA needs them at build time and the
  // same bundle serves every client. Swap them for the deployed brand's, or a
  // CPH tab shows COR's mark next to CPH's name.
  const id = b.id || 'cor';
  const icons = [
    ['link[rel="icon"][sizes="32x32"]', `/brands/${id}/favicon-32.png`],
    ['link[rel="icon"][sizes="192x192"]', `/brands/${id}/favicon-192.png`],
    ['link[rel="apple-touch-icon"]', `/brands/${id}/favicon-180.png`],
  ];
  icons.forEach(([selector, href]) => {
    const el = document.querySelector(selector);
    if (el) {
      // Browsers cache the old icon hard; re-inserting the node forces a refetch.
      const replacement = el.cloneNode();
      replacement.setAttribute('href', href);
      el.parentNode.replaceChild(replacement, el);
    }
  });

  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta && c.primary) themeMeta.setAttribute('content', c.primary);

  const desc = document.querySelector('meta[name="description"]');
  if (desc) desc.setAttribute('content', `${appName()} — ${orgName()}`);
}

/** Fetch the brand and apply it. Never throws — falls back to the default brand. */
export async function loadBrand() {
  try {
    const response = await fetch(`${API_BASE_URL}/public-config`, {
      credentials: 'include',
    });
    if (response.ok) {
      const data = await response.json();
      if (data && data.brand && data.brand.org && data.brand.org.name) {
        current = data.brand;
      }
    }
  } catch (e) {
    // offline / backend down — keep the fallback rather than blocking boot
  }
  applyBrand(current);
  return current;
}
